import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type DatabaseEntry, type DatabaseField } from '@databases/types/database';
import { type TableV2Projection } from '@databases/view-layout-table-v2/data';

interface TableV2SelectionDatabaseAdapterOptions {
  readonly projection: TableV2Projection | undefined;
  readonly getEntry: (entryId: string) => DatabaseEntry | undefined;
  readonly getSpaceKey: () => string;
}

/**
 * Uses the same projection order as the read-only Table V2 render model.
 * Entry entities stay lazy so initializing selections does not normalize every
 * value in a large database.
 */
export class TableV2SelectionDatabaseAdapter implements DatabaseAdapter {
  readonly #getEntry: TableV2SelectionDatabaseAdapterOptions['getEntry'];
  readonly #getSpaceKey: () => string;
  readonly #allFieldIds: readonly string[];
  readonly #visibleFieldIds: readonly string[];
  readonly #visibleEntryIds: readonly string[];
  readonly #fieldsById: ReadonlyMap<string, DatabaseField>;

  constructor({
    projection,
    getEntry,
    getSpaceKey,
  }: TableV2SelectionDatabaseAdapterOptions) {
    this.#getEntry = getEntry;
    this.#getSpaceKey = getSpaceKey;
    this.#allFieldIds = projection?.fields.map(({ id }) => id) ?? [];
    this.#visibleFieldIds = projection?.visibleFieldIds ?? [];
    this.#visibleEntryIds = projection?.entryIds ?? [];
    this.#fieldsById = new Map(projection?.fields.map((field) => [field.id, field]));
  }

  getVisibleFieldIds(): string[] {
    return [...this.#visibleFieldIds];
  }

  getVisibleEntryIds(): string[] {
    return [...this.#visibleEntryIds];
  }

  getVisibleEntryCount(): number {
    return this.#visibleEntryIds.length;
  }

  getVisibleEntryIdAt(index: number): string | undefined {
    return index < 0 ? undefined : this.#visibleEntryIds[index];
  }

  getVisibleFieldIdAt(index: number): string | undefined {
    return index < 0 ? undefined : this.#visibleFieldIds[index];
  }

  getFieldById(fieldId: string): DatabaseField | undefined {
    return this.#fieldsById.get(fieldId);
  }

  getEntryById(entryId: string): DatabaseEntry | undefined {
    return this.#getEntry(entryId);
  }

  getSpaceKey(): string {
    return this.#getSpaceKey();
  }

  getAllFieldIds(): string[] {
    return [...this.#allFieldIds];
  }

  canEditValue(_fieldId: string, _entryId: string): boolean {
    return false;
  }

  canRequestValueEdit(_fieldId: string, _entryId: string): boolean {
    return false;
  }

  canBulkEditValue(_fieldId: string, _entryIds: string[]): boolean {
    return false;
  }
}
